import { Directive, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ListStateStackService, ListState } from 'app/core/services/list/list-state-stack.service';
import { NavigationHistoryService } from 'app/core/services/navigation/navigation-history.service';

/**
 * Mapping of list routes to their corresponding detail route patterns
 * Used to determine if navigating from a detail page should preserve list state
 */
const LIST_DETAIL_MAPPING: Record<string, string[]> = {
  'organizations': ['/organization/detail'],
  'work-list': ['/work-list/detail', '/patient/detail'],
  'appointment-list': ['/patient/edit'], // Appointments can go to patient edit
  'patient-list': ['/patient/detail', '/patient/edit'],
  'user-list': ['/user/edit', '/user/detail'],
  'role-list': ['/role/edit', '/role/detail'],
};

@Directive()
export abstract class BaseListComponent implements OnInit, OnDestroy {
  currentPage = 1;
  pageSize = 10;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  filterForm: any = {};
  protected stateWasRestored = false;
  protected collapseElementId: string | null = null; // To be set by child components
  protected collapseIsOpen = false; // Track collapse state

  protected router = inject(Router);
  protected destroy$ = new Subject<void>();
  private navHistoryService = inject(NavigationHistoryService);

  constructor(
    protected listStateStack: ListStateStackService,
    protected routeName: string, // e.g., 'wound-consultation'
  ) {}

  ngOnInit() {
    const previousUrl = this.navHistoryService.getPreviousUrl();
    this.clearStateIfNavigatingFromDifferentList(previousUrl);
    this.restoreStateIfAvailable();
    
    const validPageSizes = [10, 15, 25, 50, 100];
    if (!validPageSizes.includes(this.pageSize)) {
      this.pageSize = 10;
    }
    
    // Call loadData if state was restored, so filters are applied
    if (this.stateWasRestored) {
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.saveState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected restoreCollapseState(): void {
    if (!this.collapseElementId) {
      return;
    }

    setTimeout(() => {
      const collapseElement = document.getElementById(this.collapseElementId!);
      if (!collapseElement || !(window as any).bootstrap) {
        return;
      }

      const collapse = (window as any).bootstrap.Collapse.getOrCreateInstance(collapseElement, { toggle: false });
      
      const originalTransition = collapseElement.style.transition;
      collapseElement.style.transition = 'none';

      if (this.collapseIsOpen) {
        collapse.show();
      } else {
        collapse.hide();
      }

      setTimeout(() => {
        collapseElement.style.transition = originalTransition;
      }, 0);
    }, 50);
  }

  protected updateCollapseState(isOpen: boolean): void {
    this.collapseIsOpen = isOpen;
  }

  /**
   * Clear state if navigating from a different list
   * This prevents filter confusion when switching between lists
   * Detail pages that map to this list will NOT clear state
   */
  private clearStateIfNavigatingFromDifferentList(previousUrl: string): void {
    if (!previousUrl) {
      return;
    }

    // Check if previous URL is a detail page of this same list
    const detailRoutes = LIST_DETAIL_MAPPING[this.routeName] || [];
    const isDetailPageOfThisList = detailRoutes.some((route) => previousUrl.includes(route));
    
    if (isDetailPageOfThisList) {
      return;
    }
    
    // Check if the previous route was another list
    const listPaths = ['patient', 'user', 'wound', 'appointment', 'work-list', 'organization', 'consultation', 'role'];
    const isFromDifferentList = listPaths.some(
      (path) => previousUrl.includes(path) && !previousUrl.includes(this.routeName)
    );
    
    if (isFromDifferentList) {
      this.listStateStack.clearStack(this.routeName);
    }
  }

  /**
   * Restore list state from stack if available
   */
  protected restoreStateIfAvailable(): void {
    if (this.listStateStack.hasState(this.routeName)) {
      const savedState = this.listStateStack.popState(this.routeName);
      if (savedState) {
        this.currentPage = savedState.currentPage;
        this.pageSize = savedState.pageSize;
        this.sortBy = savedState.sortBy;
        this.sortDirection = savedState.sortDirection;
        // Restore collapse state
        this.collapseIsOpen = savedState.collapseIsOpen ?? false;
        // Update existing filterForm properties instead of replacing the object
        // This ensures ngModel bindings update properly
        if (savedState.filters) {
          Object.assign(this.filterForm, savedState.filters);
        }
        this.stateWasRestored = true;
      }
    }
  }

  /**
   * Save current list state before navigating away
   */
  protected saveState(): void {
    this.listStateStack.pushState(this.routeName, {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      filters: this.filterForm,
      collapseIsOpen: this.collapseIsOpen,
    });
  }

  /**
   * Must be implemented by child components
   */
  abstract loadData(): void;
}
